// src/shared/composables/useSecretForm.ts

import {
  MAX_VIEWS_LIMIT,
  parseRecipientList,
  recipientListField,
} from '@/schemas/shapes/v3/delivery-options'; // Eightpoint
import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
import { storeToRefs } from 'pinia';
import { reactive } from 'vue';
import { z } from 'zod';

export interface SecretFormState {
  form: SecretFormData;
  validation: {
    errors: Map<keyof SecretFormData, string>;
    validate: () => boolean;
    validateRecipient: () => boolean;
  };
  operations: {
    updateField: <K extends keyof SecretFormData>(field: K, value: SecretFormData[K]) => void;
    reset: () => void;
  };
}

/**
 * Form validation schema
 */
const formSchema = z.object({
  secret: z.string().min(1, 'Secret content is required'),
  ttl: z.number().min(1, 'Expiration time is required'),
  passphrase: z.string(),
  // Eightpoint: several recipients (one link each), view limit, verification
  recipient: recipientListField,
  max_views: z.number().int().min(1).max(MAX_VIEWS_LIMIT).optional(),
  verify_recipient: z.boolean().optional(),
  share_domain: z.string(),
  team_id: z.string().optional(),
});

/**
 * Form data structure with defaults
 */
export type SecretFormData = z.infer<typeof formSchema>;

/**
 * useSecretForm - secret form state and validation
 *
 * Central source of truth for form state. Manages form data validation,
 * updates, and schema enforcement. Provides type-safe interface for form
 * operations while maintaining a predictable state shape.
 *
 * Responsibilities:
 * - Form state management
 * - Schema validation
 * - Type safety
 * - Field updates
 * - Form reset
 */
/* eslint-disable max-lines-per-function */
export function useSecretForm() {
  const bootstrapStore = useBootstrapStore();
  const { secret_options } = storeToRefs(bootstrapStore);

  /**
   * Creates default form state
   */
  function getDefaultFormState(): SecretFormData {
    return {
      secret: '',
      ttl: secret_options.value?.default_ttl ?? 3600 * 24 * 7,
      passphrase: '',
      recipient: '',
      max_views: 1, // Eightpoint
      verify_recipient: false, // Eightpoint
      share_domain: '',
    };
  }

  const form = reactive<SecretFormData>(getDefaultFormState());
  const errors = reactive(new Map<keyof SecretFormData, string>());

  const operations = {
    updateField: <K extends keyof SecretFormData>(field: K, value: SecretFormData[K]) => {
      form[field] = value;
    },
    reset: () => Object.assign(form, getDefaultFormState()),
  };

  return {
    form,
    validation: {
      errors,
      // eslint-disable-next-line complexity
      validate: () => {
        const result = formSchema.safeParse(form);
        errors.clear();
        if (!result.success) {
          result.error.issues.forEach((err: any) => {
            if (err.path[0]) {
              errors.set(err.path[0] as keyof SecretFormData, err.message);
            }
          });
        }

        // Eightpoint: verification is only possible with a recipient
        if (form.verify_recipient && parseRecipientList(form.recipient).length === 0) {
          errors.set('verify_recipient', 'Email verification needs at least one recipient.');
        }

        // Additional passphrase validation based on configuration
        const passphraseConfig = secret_options.value?.passphrase;

        if (passphraseConfig) {
          // Check if passphrase is required
          if (passphraseConfig.required && !(form.passphrase as string).trim()) {
            errors.set('passphrase', 'A passphrase is required for all secrets');
          }

          // Check minimum length if passphrase is provided and minimum_length is configured
          if (
            form.passphrase &&
            passphraseConfig.minimum_length &&
            (form.passphrase as string).length < passphraseConfig.minimum_length
          ) {
            errors.set(
              'passphrase',
              `Passphrase must be at least ${passphraseConfig.minimum_length} characters long`
            );
          }

          // Check maximum length if passphrase is provided and maximum_length is configured
          if (
            form.passphrase &&
            passphraseConfig.maximum_length &&
            (form.passphrase as string).length > passphraseConfig.maximum_length
          ) {
            errors.set(
              'passphrase',
              `Passphrase must be no more than ${passphraseConfig.maximum_length} characters long`
            );
          }

          // Check complexity if required and passphrase is provided
          if (form.passphrase && passphraseConfig.enforce_complexity) {
            const complexityErrors = [];
            const passphrase = form.passphrase as string;
            if (!/[A-Z]/.test(passphrase)) complexityErrors.push('uppercase letter');
            if (!/[a-z]/.test(passphrase)) complexityErrors.push('lowercase letter');
            if (!/\d/.test(passphrase)) complexityErrors.push('number');
            if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(passphrase))
              complexityErrors.push('symbol');

            if (complexityErrors.length > 0) {
              errors.set(
                'passphrase',
                `Passphrase must contain at least one ${complexityErrors.join(', ')}`
              );
            }
          }
        }

        return errors.size === 0;
      },

      // Recipient-only check for flows that skip full-form validation (e.g.
      // generate mode, where `secret` isn't user-typed so the full schema
      // can't pass). Recipient is still user input in that mode and still
      // gets mailed, so it needs its own format check.
      validateRecipient: () => {
        const result = formSchema.shape.recipient.safeParse(form.recipient);
        errors.delete('recipient');
        if (!result.success) {
          errors.set('recipient', result.error.issues[0]?.message ?? 'Invalid recipient email');
          return false;
        }
        return true;
      },
    },
    operations,
  };
}
